import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AGGREGATE_NAMES, CHAT_COMMANDS } from 'cpool-constants';
import { useTranslation } from 'react-i18next';

import { APICommunicator } from '~/api/APICommunicator';
import { invalidateInboxUnreadQueries } from '~/features/whatsapp/utils/invalidateInboxUnreadQueries';
import { toastChatCommandError } from '~/features/whatsapp/utils/toastChatCommandError';
import { useChainAwareInvalidation } from '~/hooks/useChainAwareInvalidation';
import useUserInfo from '~/hooks/useUserInfo';
import { queryKeys } from '~/utils/queryKeys';

// Gated by `canAccessChat` — for unlinked-inbox triage and manual corrections (not the startChat path).
export const useLinkChatMutation = (chatId: string) => {
    const queryClient = useQueryClient();
    const { t } = useTranslation();
    const user = useUserInfo();
    const invalidateOnChainComplete = useChainAwareInvalidation();

    return useMutation({
        mutationFn: (loanRequestId: string) =>
            APICommunicator.sendCommand({
                aggregateId: chatId,
                aggregateName: AGGREGATE_NAMES.CHAT,
                commandName: CHAT_COMMANDS.linkChat,
                payload: { loanRequestId },
            }),
        onError: (error) => {
            toastChatCommandError(error, t, 'WHATSAPP_CHAT.ERROR.LINK_FAILED');
        },
        onSuccess: ({ id: correlationId }) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.getChats() });
            if (user) {
                invalidateInboxUnreadQueries(queryClient, user.id);
            }

            invalidateOnChainComplete({
                correlationId,
                queryKeys: user
                    ? [queryKeys.getChats(), queryKeys.getInboxUnreadSummary(user.id)]
                    : [queryKeys.getChats()],
            });
        },
    });
};
